import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { OrderSideEnum } from '../../lib/enums/order-side.enum';
import { OrderStatusEnum } from '../../lib/enums/order-status.enum';
import lang from '../../lib/language';
import { CompanyLogoAndNameState } from '../../lib/models/fundamental/types';
import { Order } from '../../lib/models/trading/types';
import { RootState } from '../../lib/store/index';
import { convertOrderSideToText, orderStatusToCallStatus } from '../../lib/util/TradingHelpers';
import { TypedObject } from '../../lib/util/types';

import {
  buildOrderResultMessages,
  getNameByOrderType,
  modifyOrderPopUpBody,
  modifyOrderPopUpTitle,
  proccessPartiallyRejectedMessage,
} from '../OrderActions/OrderHelpers';
import useNewOrderTimer from '../OrderActions/useNewOrderTimer';
import FinalPopupOrderMobile from '../OrderPopupsMobile/FinalPopupOrderMobile';

type Props = {
    symbol: string;
    notEnoughStocks: boolean;
    closeModal: () => void;
    isModify?: boolean;
}

const OrderResultModal = ({ notEnoughStocks, symbol, closeModal, isModify }: Props) => {
  const companyLogoAndName = useSelector<RootState, TypedObject<CompanyLogoAndNameState>>(
    (state: RootState) => state.fundamental.companyLogoAndName,
  );

  const submittedOrder = useSelector<RootState, Order>((state: RootState) => state.trading.order);
  const requestStatus = useSelector<RootState, OrderStatusEnum>((state: RootState) => state.trading.requestStatus);

  const { ordType, orderQty, side, status, filledSoFar, traceIdentifier, rejectReport } = submittedOrder;
  const { showFillOrder, showExpired } = useNewOrderTimer(true, ordType, status, requestStatus);
  const logo = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.logo : undefined), [companyLogoAndName, symbol]);

  const url = useMemo(() => (companyLogoAndName[symbol]
    ? companyLogoAndName[symbol]?.url : undefined), [companyLogoAndName, symbol]);
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const partiallyRejectedOrder = status === OrderStatusEnum.PartiallyReject;

  const renderResponse = () => {
    if (isModify) {
      if (traceIdentifier === OrderStatusEnum.Modified) {
        return (
          <FinalPopupOrderMobile
            logo={logo}
            logoURL={url}
            callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
            processFailedMessage={lang.orderMessageBodyModifyFail()}
            processPartiallyRejectedMessage={partiallyRejectedOrder
              ? proccessPartiallyRejectedMessage(symbol, ordType!, side!, orderQty!, filledSoFar) : undefined}
            processSuccessMessageTitle={modifyOrderPopUpTitle(
              side === OrderSideEnum.Buy ? OrderSideEnum.Buy : OrderSideEnum.Sell,
              showFillOrder,
              symbol,
          ordType!,
          status,
          orderQty,
            )}
            processSuccessMessageBody={modifyOrderPopUpBody(showFillOrder, symbol, filledSoFar, status, orderQty)}
            defaultMessageTitle={orderInProgress ? lang.orderMessageTitleModify(
              symbol,
              getNameByOrderType(ordType),
              convertOrderSideToText(side),
            ) : ''}
            toggleModal={() => {
              document.body.classList.remove('modal-open');
              closeModal();
            }}
          />
        );
      }
      return (
        <FinalPopupOrderMobile
          logo={logo}
          logoURL={url}
          {...buildOrderResultMessages(symbol, submittedOrder, requestStatus, { showExpired, showFillOrder })}
          toggleModal={() => {
            document.body.classList.remove('modal-open');
            closeModal();
          }}
        />
      );
    }
    return (
      <FinalPopupOrderMobile
        logo={logo}
        logoURL={url}
        callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
        processSuccessMessageTitle={
        lang.orderMessageCancelSuccessWeb(
          getNameByOrderType(ordType),
          convertOrderSideToText(side),
          orderQty ?? 0,
          symbol,
        )
      }
        processFailedMessage={lang.orderMessageBodyCancelFail()}
        processPendingMessage={
        status === OrderStatusEnum.PendingCancelOrModify ? {
          title: lang.orderMessageTitleCancel(getNameByOrderType(ordType), convertOrderSideToText(side)),
          body: lang.orderMessageBodyCancelPending(orderQty!, symbol),
        } : undefined
      }
        defaultMessageTitle={orderInProgress ? lang.orderMessageTitleCancel(
          getNameByOrderType(ordType),
          convertOrderSideToText(side),
        ) : ''}
        defaultMessageBody={orderInProgress ? lang.orderMessageBodyCancelPending(orderQty!, symbol) : ''}
        toggleModal={() => {
          document.body.classList.remove('modal-open');
          closeModal();
        }}
      />
    );
  };

  return (
    <>
      {notEnoughStocks && (
        <FinalPopupOrderMobile
          logo={logo}
          logoURL={url}
          processSuccessMessageTitle=""
          callStatus={orderStatusToCallStatus(OrderStatusEnum.Rejected)}
          processFailedMessage={lang.webTradingIncorrectStocks()}
          toggleModal={() => {
            document.body.classList.remove('modal-open');
            closeModal();
          }}
        />
      )}

      {!notEnoughStocks && renderResponse()}
    </>
  );
};

export default OrderResultModal;
